/**
 * AutomationConditionRowContainer — binds one condition's edit handlers to its
 * stable list key (SE-5506).
 *
 * The builder owns the conditions array and passes down key-addressed callbacks
 * (`onChange(key, patch)` / `onRemove(key)`); this container curries them to
 * THIS row's key via `useCallback`, so the pure View receives stable no-arg /
 * single-value handlers and the repo's `jsx-no-new-function-as-prop` rule is
 * never tripped by an inline arrow. Changing the condition type also clears the
 * now-irrelevant value fields so a stale numeric/text/stage value can't leak
 * into the saved input.
 * @remarks Container component — owns the per-row handler binding.
 */

import { memo, useCallback, useMemo } from "react";

import type {
  PipelineAutomationComparator,
  PipelineAutomationConditionType,
} from "@/generated/graphql";

import type { AutomationRuleConditionForm } from "../../../types/automationRules";
import {
  CONDITION_VALUE_KIND,
  type ConditionValueKind,
  defaultComparatorForConditionType,
} from "../../../utils/automationRuleOptions";
import type { RuleSelectOption } from "../RuleSelectField";
import AutomationConditionRowView from "./AutomationConditionRowView";

/** Props for {@link AutomationConditionRowContainer}. */
export interface AutomationConditionRowContainerProps {
  /** The condition being edited. */
  readonly condition: AutomationRuleConditionForm;
  /** 1-based position for labels. */
  readonly displayIndex: number;
  /** Condition-type options (full enum). */
  readonly conditionTypeOptions: readonly RuleSelectOption[];
  /** Comparator options (full enum). */
  readonly comparatorOptions: readonly RuleSelectOption[];
  /** Stage options for a stage-occupancy condition. */
  readonly stageOptions: readonly RuleSelectOption[];
  /** Patch this condition (key-addressed). */
  readonly onChange: (
    key: string,
    patch: Partial<AutomationRuleConditionForm>
  ) => void;
  /** Remove this condition (key-addressed). */
  readonly onRemove: (key: string) => void;
}

/**
 * Container for one condition editor row.
 * @param props - {@link AutomationConditionRowContainerProps}
 * @param props.condition - The condition being edited
 * @param props.displayIndex - 1-based position for labels
 * @param props.conditionTypeOptions - Condition-type options (full enum)
 * @param props.comparatorOptions - Comparator options (full enum)
 * @param props.stageOptions - Stage options for a stage-occupancy condition
 * @param props.onChange - Patch this condition (key-addressed)
 * @param props.onRemove - Remove this condition (key-addressed)
 * @returns The wired {@link AutomationConditionRowView}.
 */
const AutomationConditionRowContainer = ({
  condition,
  displayIndex,
  conditionTypeOptions,
  comparatorOptions,
  stageOptions,
  onChange,
  onRemove,
}: AutomationConditionRowContainerProps) => {
  const valueKind: ConditionValueKind =
    CONDITION_VALUE_KIND[condition.conditionType];

  const handleConditionTypeChange = useCallback(
    (value: string) => {
      const nextType = value as PipelineAutomationConditionType;
      const nextKind = CONDITION_VALUE_KIND[nextType];
      // Reset value fields that don't belong to the new condition's kind so a
      // stale value can't ride along into the saved input. The comparator is
      // seeded from the shared default so this path and `makeEmptyCondition`
      // can never disagree (SE-6531).
      onChange(condition.key, {
        conditionType: nextType,
        comparator: defaultComparatorForConditionType(nextType),
        numericValue: nextKind === "numeric" ? condition.numericValue : "",
        textValue: nextKind === "text" ? condition.textValue : "",
        kanbanPhaseId: nextKind === "stage" ? condition.kanbanPhaseId : null,
      });
    },
    [
      condition.key,
      condition.numericValue,
      condition.textValue,
      condition.kanbanPhaseId,
      onChange,
    ]
  );

  const handleComparatorChange = useCallback(
    (value: string) =>
      onChange(condition.key, {
        comparator: value as PipelineAutomationComparator,
      }),
    [condition.key, onChange]
  );

  const handleNumericValueChange = useCallback(
    (value: string) => onChange(condition.key, { numericValue: value }),
    [condition.key, onChange]
  );

  const handleTextValueChange = useCallback(
    (value: string) => onChange(condition.key, { textValue: value }),
    [condition.key, onChange]
  );

  const handleStageChange = useCallback(
    (value: string) => onChange(condition.key, { kanbanPhaseId: value }),
    [condition.key, onChange]
  );

  const handleRemove = useCallback(
    () => onRemove(condition.key),
    [condition.key, onRemove]
  );

  const comparatorValue = useMemo(
    () => condition.comparator ?? "",
    [condition.comparator]
  );

  return (
    <AutomationConditionRowView
      conditionType={condition.conditionType}
      valueKind={valueKind}
      comparator={comparatorValue}
      numericValue={condition.numericValue}
      textValue={condition.textValue}
      stageValue={condition.kanbanPhaseId ?? ""}
      conditionTypeOptions={conditionTypeOptions}
      comparatorOptions={comparatorOptions}
      stageOptions={stageOptions}
      onConditionTypeChange={handleConditionTypeChange}
      onComparatorChange={handleComparatorChange}
      onNumericValueChange={handleNumericValueChange}
      onTextValueChange={handleTextValueChange}
      onStageChange={handleStageChange}
      onRemove={handleRemove}
      displayIndex={displayIndex}
    />
  );
};

AutomationConditionRowContainer.displayName = "AutomationConditionRowContainer";

export default memo(AutomationConditionRowContainer);
